
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, Plus, Trash2, User as UserIcon, Users, AlertTriangle, AlertCircle, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { FlightBooking } from '@/api/entities';
import { Passenger } from '@/api/entities';
import { SavedPassenger } from '@/api/entities';
import { User } from '@/api/entities';
import { getServiceFeeRate, calculateServiceFee, getServiceFeeLabel } from '@/utils';
import { resolveCarrierBranding, normalizeAirlineName } from '@/utils/airlineBranding';
import { useInstallmentsCopy } from '@/hooks/useInstallmentsCopy';
import { getDefaultSeatPreference } from '@/utils/seatPreference';
import { fmtAmount } from '@/utils/money';
import { TRIP_REWARD_CATEGORIES } from '@/constants/tripRewards';
import { rewardsToSave, tripRewardsOf } from '@/utils/tripRewards';

export default function AdminEditModal({ isOpen, onClose, trip, onSave }) {
  const ic = useInstallmentsCopy();
  const [editingTrip, setEditingTrip] = useState([]); // Renamed from editableFlights
  const [passengers, setPassengers] = useState([]);
  const [savedPassengers, setSavedPassengers] = useState([]);
  const [selectedSavedPassenger, setSelectedSavedPassenger] = useState('');
  const [isLoading, setIsLoading] = useState(false); // This isLoading is for save/delete operations
  const [isFetchingPassengers, setIsFetchingPassengers] = useState(true); // New state for initial data fetch
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { passengerId, index, passengerName }
  
  // Flight change tracking
  const [showFlightChanges, setShowFlightChanges] = useState(false);
  const [changedFlights, setChangedFlights] = useState([]);

  // State for pricing
  const [totalPrice, setTotalPrice] = useState(0);
  const [serviceFee, setServiceFee] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [holdFee, setHoldFee] = useState(0);
  const [grandTotalWithHold, setGrandTotalWithHold] = useState(0);
  const [serviceFeeRate, setServiceFeeRate] = useState(0.04);
  
  // Custom title for trip display
  const [customTitle, setCustomTitle] = useState('');
  
  // Cabin class override
  const [cabinClass, setCabinClass] = useState('');
  
  // Final repayment date for payment tracking
  const [finalRepaymentDate, setFinalRepaymentDate] = useState('');
  
  // Actual cost (what we paid for the flight; overrides base fare in Accounting/Analytics when set)
  const [actualCost, setActualCost] = useState('');
  
  // Flight credit (stored as cents in _change_flight_credit_cents, displayed as dollars)
  const [flightCredit, setFlightCredit] = useState('');
  
  // Amount refunded (e.g. customer cancelled and admin issued a partial cash
  // refund). Subtracted from recognized profit in Accounting/Analytics so the
  // trip's margin reflects what Defera actually kept.
  const [amountRefunded, setAmountRefunded] = useState('');
  
  // Backer selection
  const [backers, setBackers] = useState([]);
  const [selectedBackerId, setSelectedBackerId] = useState('none');

  /**
   * Defera Zero / Defera Capital trips have their billing handled entirely by Stripe
   * (subscription `unit_amount` + `metadata.total_amount_cents`). The booking row's
   * `total_amount`, `service_fee`, `hold_fee`, `grand_total`, `grand_total_with_hold`
   * are display mirrors written once by `defera-{zero,capital}-create-subscription`
   * with `total_amount = grand_total = grand_total_with_hold = trip's full Stripe-tracked
   * grand total`.
   *
   * The pricing inputs in this modal treat `total_amount` as the **base fare** and recompute
   * the rest from it; on save, that turns the trip into a self-amplifying corruption (each
   * Save inflates the displayed grand total by ~24%). For these payment methods we lock the
   * pricing card and exclude pricing fields from the save payload — Stripe is the source of
   * truth and the Cost Breakdown derives line items from `flight_data.base_amount`.
   */
  const isDeferaSubscriptionTrip = useMemo(() => {
    const fd = trip?.[0]?.flightData || trip?.[0]?.flight_data || {};
    const pm = fd._payment_method || fd.payment_method;
    return pm === 'defera_zero' || pm === 'defera_capital';
  }, [trip]);

  // Miles award overlay - shown after saving with status Completed
  const [showMilesAwardOverlay, setShowMilesAwardOverlay] = useState(false);
  const [milesAwardBookingId, setMilesAwardBookingId] = useState(null);
  const [milesAwardLoading, setMilesAwardLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setShowMilesAwardOverlay(false);
      setMilesAwardBookingId(null);
    }
  }, [isOpen]);

  // Track whether we've initialized for this modal-open session.
  // Only reset editingTrip when modal first opens, NOT when trip changes mid-edit.
  // This prevents the confirmation number (and other fields) from being overwritten
  // when the parent refetches after save, which was causing the input to appear "stuck".
  const hasInitializedForSessionRef = useRef(false);

  /**
   * Tracks whether the admin actually edited the "Total Trip Price" input in this
   * modal-open session. Stays `false` until the input's `onChange` fires.
   *
   * Why this exists: J1TS39 regression (2026-06-09). For Slice Pay / installments
   * trips, `bookings.total_amount` does NOT cleanly mean "base fare" across the
   * row's lifetime — change-flight writes a value derived from
   * `grand_total / 1.11`, fresh entity creation writes the customer's billed
   * total, etc. The pricing recompute effect below treats `totalPrice` as the
   * base fare and re-derives fees off it; without this gate, simply opening
   * the modal and clicking Save (e.g. to change the flight credit, status, or
   * confirmation number) silently inflates the row's `grand_total_with_hold`
   * by ~24% on every save — exactly the same corruption pattern the
   * `isDeferaSubscriptionTrip` lock prevents for Defera Zero/Capital trips.
   *
   * Behavior: the recompute effect and the save path now both consult this
   * ref. Pricing in display + DB stays at the stored values until the admin
   * explicitly edits the Total Trip Price input.
   */
  const hasAdminEditedPricingRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      hasInitializedForSessionRef.current = false;
      hasAdminEditedPricingRef.current = false;
      return;
    }
    if (!trip || trip.length === 0) return;

    // Don't re-initialize when trip changes while modal is open (e.g. parent refetch after save)
    if (hasInitializedForSessionRef.current) return;
    hasInitializedForSessionRef.current = true;
    
    // Extract individual flight records from booking objects
      // A booking may have multiple slices (e.g., roundtrip has outbound + return slices)
      const extractedFlights = [];
      trip.forEach(booking => {
        const slices = booking.flightData?.slices || [];
        if (slices.length > 0) {
          // Create flight objects from each slice
          slices.forEach(slice => {
            if (slice.segments && slice.segments.length > 0) {
              const firstSegment = slice.segments[0];
              const lastSegment = slice.segments[slice.segments.length - 1];
              
              // Helper to parse date/time safely
              const parseDateTime = (dateTimeStr) => {
                if (!dateTimeStr) return { date: '', time: '' };
                // Handle ISO format: 2025-11-13T19:30
                if (dateTimeStr.includes('T')) {
                  const [date, time] = dateTimeStr.split('T');
                  return { date, time: time?.substring(0, 5) || '' };
                }
                // Handle space-separated: 2025-11-13 19:30 (or corrupted duplicates)
                const parts = dateTimeStr.trim().split(' ');
                if (parts.length >= 2) {
                  return { date: parts[0], time: parts[1]?.substring(0, 5) || '' };
                }
                return { date: parts[0] || '', time: '' };
              };
              
              const departureDateTime = parseDateTime(firstSegment?.departing_at);
              const arrivalDateTime = parseDateTime(lastSegment?.arriving_at);
              
              extractedFlights.push({
                ...booking,
                departure_city: firstSegment?.origin?.iata_code || firstSegment?.origin?.city_name,
                departure_time: departureDateTime.time,
                departure_date: departureDateTime.date,
                arrival_city: lastSegment?.destination?.iata_code || lastSegment?.destination?.city_name,
                arrival_time: arrivalDateTime.time,
                arrival_date: arrivalDateTime.date,
                marketing_airline: firstSegment?.marketing_carrier?.name,
                flight_number: firstSegment?.marketing_carrier_flight_number || firstSegment?.flight_number,
                sliceData: slice // Keep original slice data
              });
            }
          });
        } else {
          // Fallback: use booking's extracted fields (already populated by FlightBooking entity)
          extractedFlights.push(booking);
        }
      });

      // Deep copy the extracted flights
      const initialFlights = JSON.parse(JSON.stringify(extractedFlights));

      // Ensure the first flight has is_primary_flight set for trip_status and pricing handling
      if (initialFlights.length > 0) {
        initialFlights[0].is_primary_flight = true;
        // Ensure confirmation_number is on first flight (may be in flight_data or on booking)
        const confFromBooking = trip[0]?.confirmationNumber || trip[0]?.confirmation_number;
        const confFromFlightData = trip[0]?.flightData?.confirmation_number || trip[0]?.flight_data?.confirmation_number || trip[0]?.flightData?.airline_confirmation || trip[0]?.flight_data?.airline_confirmation;
        const confNum = confFromBooking || confFromFlightData || '';
        if (confNum && !initialFlights[0].confirmation_number) {
          initialFlights[0].confirmation_number = confNum;
          initialFlights[0].confirmationNumber = confNum;
        }
      }

      // Initialize is_ticketed from flight_data (display-only for progress bar; trip status stays Confirmed)
      const firstFlightDataForTicketed = trip[0]?.flightData || trip[0]?.flight_data || {};
      if (initialFlights.length > 0) {
        initialFlights[0].is_ticketed = firstFlightDataForTicketed.is_ticketed ?? false;
        initialFlights[0].is_agency = firstFlightDataForTicketed.is_agency ?? false;
        initialFlights[0].is_upgraded = firstFlightDataForTicketed.is_upgraded ?? false;
        initialFlights[0].upgraded_to = firstFlightDataForTicketed.upgraded_to ?? '';
        const rewards = tripRewardsOf(firstFlightDataForTicketed);
        initialFlights[0].is_rewarded = rewards.length > 0;
        initialFlights[0].complimentary_rewards = rewards;
      }

      setEditingTrip(initialFlights);
      
      // Initialize flight changes if they exist (stored inside flight_data)
      const flightData = trip[0]?.flightData || trip[0]?.flight_data;
      const existingFlightChanges = flightData?.flight_changes;
      if (existingFlightChanges && existingFlightChanges.slices && existingFlightChanges.slices.length > 0) {
        setShowFlightChanges(true);
        
        // Extract changed flights from flight_changes
        const changedFlightsList = existingFlightChanges.slices.map((slice, index) => {
          const firstSegment = slice.segments?.[0];
          const lastSegment = slice.segments?.[slice.segments.length - 1];
          
          const parseDateTime = (dateTimeStr) => {
            if (!dateTimeStr) return { date: '', time: '' };
            if (dateTimeStr.includes('T')) {
              const [date, time] = dateTimeStr.split('T');
              return { date, time: time?.substring(0, 5) || '' };
            }
            const parts = dateTimeStr.trim().split(' ');
            if (parts.length >= 2) {
              return { date: parts[0], time: parts[1]?.substring(0, 5) || '' };
            }
            return { date: parts[0] || '', time: '' };
          };
          
          const departureDateTime = parseDateTime(firstSegment?.departing_at);
          const arrivalDateTime = parseDateTime(lastSegment?.arriving_at);
          
          return {
            departure_city: firstSegment?.origin?.iata_code || firstSegment?.origin?.city_name,
            departure_time: departureDateTime.time,
            departure_date: departureDateTime.date,
            arrival_city: lastSegment?.destination?.iata_code || lastSegment?.destination?.city_name,
            arrival_time: arrivalDateTime.time,
            arrival_date: arrivalDateTime.date,
            marketing_airline: firstSegment?.marketing_carrier?.name,
            flight_number: firstSegment?.marketing_carrier_flight_number || firstSegment?.flight_number,
          };
        });
        
        setChangedFlights(changedFlightsList);
      } else {
        setShowFlightChanges(false);
        setChangedFlights([]);
      }

      // Pricing Initialization - look for any flight with pricing data
      // Check for totalPrice (camelCase from entity) or total_amount (database field)
      let flightWithPrice = initialFlights.find((f) => (f.totalPrice && f.totalPrice > 0) || (f.total_amount && f.total_amount > 0));
      if (!flightWithPrice) {
        // If no flight has totalPrice/total_amount, check for any flight with grand_total
        flightWithPrice = initialFlights.find((f) => f.grand_total && f.grand_total > 0);
      }
      if (!flightWithPrice) {
        // Default to first flight if no pricing found
        flightWithPrice = initialFlights[0];
      }

      const initialFlightData = trip?.[0]?.flightData || trip?.[0]?.flight_data || {};
      const initialPm = initialFlightData._payment_method || initialFlightData.payment_method;
      const isDeferaSubTrip = initialPm === 'defera_zero' || initialPm === 'defera_capital';
      /**
       * Defera Zero/Capital: prefer `flight_data.base_amount` (the canonical Duffel ticket
       * price written at booking) over the booking row's `total_amount` (which for these
       * payment methods is actually the post-fee grand total — see comment above on
       * `isDeferaSubscriptionTrip`). Falling back to `total_amount` would re-introduce the
       * "treat grand total as base, then re-add fees on top" amplification bug that produced
       * 847K5R's drift.
       */
      const flightDataBase = parseFloat(initialFlightData.base_amount ?? 0) || 0;
      const basePrice = isDeferaSubTrip && flightDataBase > 0
        ? flightDataBase
        : (flightWithPrice?.totalPrice || flightWithPrice?.total_amount || 0);
      const useDeferaCapitalTier = initialPm === 'defera_capital';
      const calculatedServiceFeeRate = getServiceFeeRate(basePrice, useDeferaCapitalTier);
      const existingServiceFee = isDeferaSubTrip
        ? calculateServiceFee(basePrice, useDeferaCapitalTier)
        : (flightWithPrice?.service_fee || calculateServiceFee(basePrice, false));
      const existingGrandTotal = isDeferaSubTrip
        ? basePrice + existingServiceFee
        : (flightWithPrice?.grand_total || basePrice + existingServiceFee);
      const existingHoldFee = isDeferaSubTrip
        ? existingGrandTotal * 0.15
        : (flightWithPrice?.hold_fee || existingGrandTotal * 0.15);
      const existingGrandTotalWithHold = isDeferaSubTrip
        ? existingGrandTotal + existingHoldFee
        : (flightWithPrice?.grand_total_with_hold || existingGrandTotal + existingHoldFee);

      setTotalPrice(basePrice);
      setServiceFeeRate(calculatedServiceFeeRate);
      setServiceFee(existingServiceFee);
      setGrandTotal(existingGrandTotal);
      setHoldFee(existingHoldFee);
      setGrandTotalWithHold(existingGrandTotalWithHold);
      
      // Initialize custom title from flight_data (stored inside JSONB to avoid schema cache issues)
      const firstFlightData = trip[0]?.flightData || trip[0]?.flight_data || {};
      setCustomTitle(firstFlightData.custom_title || trip[0]?.customTitle || trip[0]?.custom_title || '');
      
      // Initialize cabin class from flight_data
      setCabinClass(firstFlightData.cabin_class || '');
      
      // Initialize final repayment date from flight_data
      setFinalRepaymentDate(firstFlightData.final_repayment_date || '');
      
      // Initialize actual cost from flight_data (overrides base fare in Accounting when set)
      const ac = firstFlightData._actual_cost ?? firstFlightData.actual_cost;
      setActualCost(ac != null && ac !== '' ? String(ac) : '');
      
      // Initialize flight credit from flight_data (stored as cents, display as dollars)
      const creditCents = firstFlightData._change_flight_credit_cents ?? 0;
      setFlightCredit(creditCents > 0 ? String(creditCents / 100) : '');
      
      // Initialize amount refunded from flight_data (dollars)
      const refunded = firstFlightData._amount_refunded;
      setAmountRefunded(refunded != null && refunded !== '' ? String(refunded) : '');
      
      // Initialize backer selection
      const existingBackerId = trip[0]?.backer_id || trip[0]?.backerId || null;
      setSelectedBackerId(existingBackerId || 'none');

      const loadData = async () => {
        setIsFetchingPassengers(true);
        try {
          // Fetch current passengers for this trip
          // Assuming all flight bookings in a 'trip' share the same set of passengers initially
          const currentPassengers = await Passenger.findByBookingId(trip[0].id);
          setPassengers(currentPassengers);

          // Fetch saved passengers for the trip creator
          const tripCreatorEmail = trip[0].created_by;
          const userSavedPassengers = await SavedPassenger.filter({ created_by: tripCreatorEmail });
          setSavedPassengers(userSavedPassengers);
          
          // Load backers list
          const allUsers = await User.list();
          console.log('📊 [AdminEditModal] All users loaded:', allUsers.length);
          console.log('📊 [AdminEditModal] Sample user data:', allUsers.slice(0, 3).map(u => ({
            email: u.email,
            role: u.role,
            app_metadata: u.app_metadata,
            roles: u.app_metadata?.roles
          })));
          
          const backerUsers = allUsers.filter(u => {
            // Handle both array and non-array roles
            const rolesRaw = u.app_metadata?.roles;
            const roles = Array.isArray(rolesRaw) ? rolesRaw : (rolesRaw ? [rolesRaw] : []);
            const userRole = u.role;
            const isBacker = roles.includes('backer') || userRole === 'backer';
            console.log(`🔍 [AdminEditModal] Checking user ${u.email}:`, {
              rolesRaw,
              roles,
              userRole,
              app_metadata: u.app_metadata,
              isBacker
            });
            return isBacker;
          });
          console.log('📊 [AdminEditModal] Backers found:', backerUsers.length, backerUsers.map(u => u.email));
          setBackers(backerUsers);
        } catch (error) {
          console.error('Failed to load passenger data:', error);
        } finally {
          setIsFetchingPassengers(false);
        }
      };

      loadData();
  }, [isOpen, trip]);

  // Recalculate fees when total price changes
  useEffect(() => {
    /** Defera Zero/Capital: pricing is locked to Stripe/flight_data.base_amount in the data-load
     *  effect; the admin can't edit `totalPrice` for these trips (input is disabled), so this
     *  recompute would only re-derive the same canonical values. Skipping avoids any chance of a
     *  state-update loop that could overwrite the canonical pricing in edge cases. */
    if (isDeferaSubscriptionTrip) return;

    /**
     * Slice Pay / installments / standard checkout: only recompute fees when the admin has
     * actually edited the "Total Trip Price" input this session (see comment on
     * `hasAdminEditedPricingRef`). Otherwise we'd overwrite the stored fee values on
     * mount — `setTotalPrice(basePrice)` from the data-load effect triggers this hook,
     * and basePrice is read from `bookings.total_amount` which doesn't reliably mean
     * "base fare" across the row's lifetime (change-flight writes a $X/1.11 derivation,
     * etc.). For untouched-pricing sessions (admin only changing flight credit / status /
     * confirmation number), this keeps the displayed AND saved values aligned with the row.
     */
    if (!hasAdminEditedPricingRef.current) return;

    const calculatedServiceFeeRate = getServiceFeeRate(totalPrice, false);
    const calculatedServiceFee = calculateServiceFee(totalPrice, false);
    const calculatedGrandTotal = totalPrice + calculatedServiceFee;
    const calculatedHoldFee = calculatedGrandTotal * 0.15;
    const calculatedGrandTotalWithHold = calculatedGrandTotal + calculatedHoldFee;

    setServiceFeeRate(calculatedServiceFeeRate);
    setServiceFee(calculatedServiceFee);
    setGrandTotal(calculatedGrandTotal);
    setHoldFee(calculatedHoldFee);
    setGrandTotalWithHold(calculatedGrandTotalWithHold);
  }, [totalPrice, isDeferaSubscriptionTrip]);

  if (!isOpen) return null;

  const handleFlightChange = (index, field, value) => {
    const newTrip = JSON.parse(JSON.stringify(editingTrip));
    newTrip[index][field] = value;
    // Sync confirmation_number <-> confirmationNumber for consistent display
    if (field === 'confirmation_number') {
      newTrip[index].confirmationNumber = value;
    }
    // Sync top-level flight_number to first segment when present
    if (field === 'flight_number' && newTrip[index]?.sliceData?.segments?.[0]) {
      newTrip[index].sliceData.segments[0].marketing_carrier_flight_number = value;
    }
    setEditingTrip(newTrip);
  };

  // Rewarded (src/constants/tripRewards.js): the rows on the first flight.
  const blankRewardRow = () => ({ id: '', category: TRIP_REWARD_CATEGORIES[0].id, name: '', added_at: null });
  const setRewardRows = (update) => {
    const newTrip = JSON.parse(JSON.stringify(editingTrip));
    const rows = Array.isArray(newTrip[0]?.complimentary_rewards) ? newTrip[0].complimentary_rewards : [];
    newTrip[0].complimentary_rewards = update(rows);
    setEditingTrip(newTrip);
  };
  const handleRewardedToggle = (checked) => {
    const newTrip = JSON.parse(JSON.stringify(editingTrip));
    newTrip[0].is_rewarded = checked;
    if (checked && !(newTrip[0].complimentary_rewards || []).length) newTrip[0].complimentary_rewards = [blankRewardRow()];
    setEditingTrip(newTrip);
  };

  // Format ISO duration (PT5H30M) to "5 hr 30 min", or pass through raw user input
  const formatDurationForInput = (isoDuration) => {
    if (!isoDuration) return '';
    const s = String(isoDuration).trim();
    if (!s) return '';
    // If it's not ISO format, return as-is (user's raw input while typing).
    // Only auto-format real numbers (normalized layover minutes) — formatting
    // numeric *strings* would mangle input mid-keystroke ("4" → "4 min").
    if (!s.startsWith('PT')) {
      if (typeof isoDuration === 'number') {
        const m = parseInt(isoDuration);
        const h = Math.floor(m / 60);
        const min = m % 60;
        return h > 0 ? `${h} hr ${min} min`.trim() : `${min} min`;
      }
      return s;
    }
    const matches = s.match(/PT(\d+H)?(\d+M)?/);
    if (!matches) return s;
    const hours = matches[1] ? parseInt(matches[1].slice(0, -1)) : 0;
    const minutes = matches[2] ? parseInt(matches[2].slice(0, -1)) : 0;
    if (hours > 0) return `${hours} hr ${minutes} min`.trim();
    return minutes > 0 ? `${minutes} min` : '';
  };

  // Parse "5 hr 30 min", "90 min", or "265" to total minutes (layover_duration
  // is stored as raw minutes, unlike segment duration which is ISO).
  const parseDurationToMinutes = (str) => {
    if (str == null || !String(str).trim()) return null;
    const s = String(str).trim();
    if (/^\d+$/.test(s)) return parseInt(s);
    let totalMinutes = 0;
    const hrMatch = s.match(/(\d+)\s*hr/);
    const minMatch = s.match(/(\d+)\s*min/);
    if (hrMatch) totalMinutes += parseInt(hrMatch[1]) * 60;
    if (minMatch) totalMinutes += parseInt(minMatch[1]);
    return totalMinutes > 0 ? totalMinutes : null;
  };

  // Parse "5 hr 30 min" or "90 min" to PT5H30M
  const parseDurationToIso = (str) => {
    if (!str || !str.trim()) return null;
    const s = str.trim();
    let totalMinutes = 0;
    const hrMatch = s.match(/(\d+)\s*hr/);
    const minMatch = s.match(/(\d+)\s*min/);
    if (hrMatch) totalMinutes += parseInt(hrMatch[1]) * 60;
    if (minMatch) totalMinutes += parseInt(minMatch[1]);
    if (totalMinutes === 0) return null;
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `PT${h}H${m}M`;
  };

  const handleSegmentChange = (flightIndex, segmentIndex, field, value) => {
    const newTrip = JSON.parse(JSON.stringify(editingTrip));
    const slice = newTrip[flightIndex]?.sliceData;
    if (!slice?.segments?.[segmentIndex]) return;
    const seg = slice.segments[segmentIndex];
    if (field === 'layover_city') {
      if (!seg.destination) seg.destination = {};
      seg.destination.iata_code = value;
      seg.destination.city_name = value;
    } else if (field === 'duration') {
      seg.duration = value;
    } else if (field === 'layover_duration') {
      // The layover BEFORE segment i+1 lives on segment i+1 (matches
      // FlightOfferCard's `slice.segments[index + 1].layover_duration` read).
      const nextSeg = slice.segments[segmentIndex + 1];
      if (nextSeg) nextSeg.layover_duration = value;
    } else if (field === 'aircraft') {
      if (!seg.aircraft) seg.aircraft = {};
      seg.aircraft.name = value;
    } else if (field === 'flight_number') {
      seg.marketing_carrier_flight_number = value;
      // Sync to top-level when editing first segment
      if (segmentIndex === 0) {
        newTrip[flightIndex].flight_number = value;
      }
    }
    setEditingTrip(newTrip);
  };

  const handleAddSegment = (flightIndex) => {
    const newTrip = JSON.parse(JSON.stringify(editingTrip));
    const slice = newTrip[flightIndex]?.sliceData;
    if (!slice) return;
    if (!slice.segments) slice.segments = [];
    const lastSeg = slice.segments[slice.segments.length - 1];
    const lastArriving = lastSeg?.arriving_at;
    let newDeparting = lastArriving || '';
    let newArriving = '';
    if (lastArriving) {
      const parsed = lastArriving.includes('T') ? lastArriving : lastArriving.replace(' ', 'T') + ':00';
      const d = new Date(parsed);
      if (!isNaN(d.getTime())) {
        const pad = (n) => String(n).padStart(2, '0');
        d.setHours(d.getHours() + 1);
        newDeparting = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
        d.setHours(d.getHours() + 2);
        newArriving = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
      }
    }
    const newSegment = {
      id: `new-segment-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      origin: lastSeg?.destination ? JSON.parse(JSON.stringify(lastSeg.destination)) : { iata_code: '', city_name: '' },
      destination: { iata_code: '', city_name: '' },
      departing_at: newDeparting,
      arriving_at: newArriving,
      marketing_carrier: lastSeg?.marketing_carrier ? JSON.parse(JSON.stringify(lastSeg.marketing_carrier)) : { name: '' },
      marketing_carrier_flight_number: '',
      aircraft: { name: '' },
      duration: ''
    };
    slice.segments.push(newSegment);
    const firstSeg = slice.segments[0];
    const parseDt = (str) => {
      if (!str) return { date: '', time: '' };
      if (str.includes('T')) {
        const [d, t] = str.split('T');
        return { date: d, time: (t || '').substring(0, 5) };
      }
      const parts = str.trim().split(' ');
      return { date: parts[0] || '', time: (parts[1] || '').substring(0, 5) };
    };
    newTrip[flightIndex].departure_city = firstSeg?.origin?.iata_code || firstSeg?.origin?.city_name || '';
    newTrip[flightIndex].arrival_city = newSegment.destination?.iata_code || newSegment.destination?.city_name || '';
    const dep = parseDt(firstSeg?.departing_at);
    const arr = parseDt(newSegment.arriving_at);
    newTrip[flightIndex].departure_date = dep.date;
    newTrip[flightIndex].departure_time = dep.time;
    newTrip[flightIndex].arrival_date = arr.date;
    newTrip[flightIndex].arrival_time = arr.time;
    newTrip[flightIndex].marketing_airline = firstSeg?.marketing_carrier?.name || '';
    newTrip[flightIndex].flight_number = firstSeg?.marketing_carrier_flight_number || '';
    setEditingTrip(newTrip);
  };

  const handleDeleteSegment = (flightIndex, segmentIndex) => {
    const newTrip = JSON.parse(JSON.stringify(editingTrip));
    const slice = newTrip[flightIndex]?.sliceData;
    if (!slice?.segments || slice.segments.length <= 1) return;
    slice.segments.splice(segmentIndex, 1);
    const firstSeg = slice.segments[0];
    const lastSeg = slice.segments[slice.segments.length - 1];
    const parseDt = (str) => {
      if (!str) return { date: '', time: '' };
      if (str.includes('T')) {
        const [d, t] = str.split('T');
        return { date: d, time: (t || '').substring(0, 5) };
      }
      const parts = str.trim().split(' ');
      return { date: parts[0] || '', time: (parts[1] || '').substring(0, 5) };
    };
    newTrip[flightIndex].departure_city = firstSeg?.origin?.iata_code || firstSeg?.origin?.city_name || '';
    newTrip[flightIndex].arrival_city = lastSeg?.destination?.iata_code || lastSeg?.destination?.city_name || '';
    const dep = parseDt(firstSeg?.departing_at);
    const arr = parseDt(lastSeg?.arriving_at);
    newTrip[flightIndex].departure_date = dep.date;
    newTrip[flightIndex].departure_time = dep.time;
    newTrip[flightIndex].arrival_date = arr.date;
    newTrip[flightIndex].arrival_time = arr.time;
    newTrip[flightIndex].marketing_airline = firstSeg?.marketing_carrier?.name || '';
    newTrip[flightIndex].flight_number = firstSeg?.marketing_carrier_flight_number || '';
    setEditingTrip(newTrip);
  };
  
  const handleChangedFlightChange = (index, field, value) => {
    const newChangedFlights = [...changedFlights];
    if (!newChangedFlights[index]) {
      newChangedFlights[index] = {};
    }
    newChangedFlights[index][field] = value;
    setChangedFlights(newChangedFlights);
  };
  
  const initializeFlightChanges = () => {
    // Copy existing flights as base for changed flights
    const initialChangedFlights = editingTrip.map(flight => ({
      departure_city: flight.departure_city,
      departure_time: flight.departure_time,
      departure_date: flight.departure_date,
      arrival_city: flight.arrival_city,
      arrival_time: flight.arrival_time,
      arrival_date: flight.arrival_date,
      marketing_airline: flight.marketing_airline,
      flight_number: flight.flight_number,
    }));
    setChangedFlights(initialChangedFlights);
    setShowFlightChanges(true);
  };

  const handlePassengerChange = (index, field, value) => {
    const newPassengers = [...passengers];
    newPassengers[index][field] = value;
    setPassengers(newPassengers);
  };

  const addPassengerFromSaved = async () => {
    if (!selectedSavedPassenger) return;

    const savedPassenger = savedPassengers.find((p) => p.id === selectedSavedPassenger);
    if (!savedPassenger) return;

    try {
      // Create passenger record for each flight in the trip
      // For now, add to state directly, will be saved on handleSave
      setPassengers((prev) => [...prev, {
        tempId: `new-saved-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`, // Temporary ID
        bookingId: trip[0].id, // Associated with the trip's first flight for initial state
        title: savedPassenger.title || '',
        firstName: savedPassenger.firstName || savedPassenger.first_name,
        middleName: savedPassenger.middleName || savedPassenger.middle_name || '',
        lastName: savedPassenger.lastName || savedPassenger.last_name,
        bornOn: savedPassenger.bornOn || savedPassenger.date_of_birth || '',
        gender: savedPassenger.gender || '',
        passportNumber: savedPassenger.passportNumber || savedPassenger.passport_number || '',
        passportExpiryDate: savedPassenger.passportExpiryDate || savedPassenger.passport_expiry || '',
        nationality: savedPassenger.nationality || '',
        phoneNumber: savedPassenger.phoneNumber || savedPassenger.phone || '',
        email: savedPassenger.email || '',
        specialRequests: '', // Saved passengers might not have this, default to empty
        frequentFlyerNumber: savedPassenger.frequentFlyerNumber || savedPassenger.frequent_flyer_number || '',
        knownTravelerNumber: savedPassenger.knownTravelerNumber || savedPassenger.known_traveler_number || '',
        seatPreference:
          savedPassenger.seatPreference ||
          savedPassenger.seat_preference ||
          getDefaultSeatPreference(prev.length)
      }]);
      setSelectedSavedPassenger(''); // Clear selection after adding
    } catch (error) {
      console.error('Failed to add passenger from saved:', error);
    }
  };

  const addNewPassenger = () => {
    setPassengers((prev) => [...prev, {
      // Temporary ID for React key prop, will be removed before saving
      tempId: `new-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      bookingId: trip[0].id, // Associate with the trip's first flight for initial state
      title: '', firstName: '', middleName: '', lastName: '', bornOn: '',
      gender: '', passportNumber: '', passportExpiryDate: '', nationality: '',
      phoneNumber: '', email: '', specialRequests: '', frequentFlyerNumber: '', knownTravelerNumber: '',
      seatPreference: getDefaultSeatPreference(prev.length)
    }]);
  };

  const confirmDeletePassenger = (passengerId, index) => {
    const passenger = passengers[index];
    const passengerName = `${passenger.firstName} ${passenger.lastName}`;
    setDeleteConfirm({ passengerId, index, passengerName });
  };

  const removePassenger = async () => {
    if (!deleteConfirm) return;

    setIsLoading(true); // Set loading state for delete operation
    const { passengerId, index } = deleteConfirm;
    // Get the passenger object before removing it from the state
    const passengerDataBeforeRemoval = passengers[index];

    // Optimistically update UI
    setPassengers((prevPassengers) => prevPassengers.filter((_, i) => i !== index));

    // If passenger had a real ID (not a temp one), attempt to delete from DB
    if (passengerDataBeforeRemoval.id && !passengerDataBeforeRemoval.id.startsWith('new-')) {
      try {
        // Delete records across all flights that correspond to this unique passenger
        // This assumes that a single 'passenger' in the UI maps to potentially
        // multiple 'Passenger' entity records in the database, one for each flight booking.
        // We identify these related records by a unique combination of passenger details.
        for (const flight of trip) {
          const flightPassengersRecords = await Passenger.findByBookingId(flight.id);
          const recordToDelete = flightPassengersRecords.find((p) =>
          p.firstName === passengerDataBeforeRemoval.firstName &&
          p.lastName === passengerDataBeforeRemoval.lastName &&
          p.bornOn === passengerDataBeforeRemoval.bornOn &&
          p.email === passengerDataBeforeRemoval.email // Added email for better uniqueness
          );
          if (recordToDelete) {
            await Passenger.delete(recordToDelete.id);
          }
        }
      } catch (error) {
        console.error('Failed to remove passenger from DB:', error);
        // Revert optimistic update if DB deletion fails
        setPassengers((prevPassengers) => {
          const newPassengers = [...prevPassengers];
          // Insert back at original index. prevPassengers will already be the filtered state.
          newPassengers.splice(index, 0, passengerDataBeforeRemoval);
          return newPassengers;
        });
        // Optionally, show a user-facing error message
      } finally {
        setIsLoading(false); // Reset loading state
      }
    } else {
      setIsLoading(false); // If it was a temp passenger, no DB call, just clear loading
    }
    // Clear confirmation state after action
    setDeleteConfirm(null);
  };

  const handleAwardMiles = async () => {
    if (!milesAwardBookingId) return;
    setMilesAwardLoading(true);
    try {
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch('/api/admin/miles/award-for-booking', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ bookingId: milesAwardBookingId })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to award miles');
      }

      alert(`✅ Successfully awarded ${data.milesEarned?.toLocaleString() || '?'} miles to customer.`);
      setShowMilesAwardOverlay(false);
      setMilesAwardBookingId(null);
    } catch (error) {
      console.error('Award miles failed:', error);
      alert('❌ ' + (error.message || 'Failed to award miles'));
    } finally {
      setMilesAwardLoading(false);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      // Get the original booking to preserve its flight_data structure
      const originalBooking = trip[0];
      const baseFlightData = originalBooking.flightData || originalBooking.flight_data || {};
      const updatedFlightData = JSON.parse(JSON.stringify(baseFlightData));
      
      // Update each slice with edited information (including segment-level edits)
      editingTrip.forEach((editedFlight, index) => {
        if (updatedFlightData.slices && updatedFlightData.slices[index]) {
          // Use edited sliceData as base (includes aircraft, flight #, duration, layover edits)
          const editedSlice = editedFlight.sliceData;
          if (editedSlice) {
            updatedFlightData.slices[index] = JSON.parse(JSON.stringify(editedSlice));
          }
          const slice = updatedFlightData.slices[index];
          // Normalize segment durations: convert raw "1 hr 30 min" to ISO PT1H30M on save
          if (slice?.segments) {
            slice.segments.forEach((seg) => {
              if (seg.duration && !String(seg.duration).startsWith('PT')) {
                const iso = parseDurationToIso(seg.duration);
                if (iso) seg.duration = iso;
              }
              // layover_duration is stored as raw minutes (e.g. 155); normalize
              // typed input like "4 hr 25 min" to minutes on save.
              if (seg.layover_duration != null && typeof seg.layover_duration !== 'number') {
                const mins = parseDurationToMinutes(seg.layover_duration);
                if (mins != null) seg.layover_duration = mins;
              }
            });
          }
          
          if (slice.segments && slice.segments.length > 0) {
            const firstSegment = slice.segments[0];
            const lastSegment = slice.segments[slice.segments.length - 1];
            
            // Apply top-level flight overrides
            if (editedFlight.departure_city) {
              if (!firstSegment.origin) firstSegment.origin = {};
              firstSegment.origin.iata_code = editedFlight.departure_city;
              firstSegment.origin.city_name = editedFlight.departure_city;
            }
            if (editedFlight.departure_date && editedFlight.departure_time) {
              firstSegment.departing_at = `${editedFlight.departure_date} ${editedFlight.departure_time}`;
            }
            
            if (editedFlight.arrival_city) {
              if (!lastSegment.destination) lastSegment.destination = {};
              lastSegment.destination.iata_code = editedFlight.arrival_city;
              lastSegment.destination.city_name = editedFlight.arrival_city;
            }
            if (editedFlight.arrival_date && editedFlight.arrival_time) {
              lastSegment.arriving_at = `${editedFlight.arrival_date} ${editedFlight.arrival_time}`;
            }
            
            if (editedFlight.marketing_airline) {
              // Re-brand, don't just rename: iata_code + logo URLs must follow
              // the airline, or every surface keeps rendering the old
              // carrier's mark next to the new name (4FLGZU's return kept the
              // Southwest logo after a rename to Delta). Segments that were on
              // the slice's original first-segment carrier follow the rename —
              // a leg-level airline edit means "this leg is now on X" — while
              // interline segments on a different carrier are left alone.
              const newName = editedFlight.marketing_airline;
              const prevFirstCarrier = firstSegment.marketing_carrier || {};
              const prevFirstName = normalizeAirlineName(prevFirstCarrier.name);
              const prevFirstIata = String(prevFirstCarrier.iata_code || '').trim().toUpperCase();
              const branding = resolveCarrierBranding(newName);
              const applyNewCarrier = (seg) => {
                const mc = seg.marketing_carrier || (seg.marketing_carrier = {});
                const segPrevName = normalizeAirlineName(mc.name);
                const segPrevIata = String(mc.iata_code || '').trim().toUpperCase();
                // Own-metal segments (operating carrier mirrored the marketing
                // carrier) follow the rename; codeshares keep their operator.
                const ownMetal = seg.operating_carrier && (
                  (segPrevIata && String(seg.operating_carrier.iata_code || '').trim().toUpperCase() === segPrevIata)
                  || (segPrevName && normalizeAirlineName(seg.operating_carrier.name) === segPrevName)
                );
                mc.name = newName;
                if (branding) {
                  Object.assign(mc, branding);
                } else if (normalizeAirlineName(newName) !== segPrevName) {
                  // Renamed to an airline we can't resolve — the old branding
                  // is wrong for it, and a wrong logo is worse than the
                  // neutral plane-icon fallback. Drop it.
                  delete mc.iata_code;
                  delete mc.logo_symbol_url;
                  delete mc.logo_lockup_url;
                }
                if (ownMetal) {
                  seg.operating_carrier = { ...seg.operating_carrier, name: newName };
                  if (branding) {
                    Object.assign(seg.operating_carrier, branding);
                  } else {
                    delete seg.operating_carrier.iata_code;
                    delete seg.operating_carrier.logo_symbol_url;
                    delete seg.operating_carrier.logo_lockup_url;
                  }
                }
              };
              applyNewCarrier(firstSegment);
              slice.segments.slice(1).forEach((seg) => {
                const mc = seg.marketing_carrier || {};
                const matchesOriginalCarrier =
                  (prevFirstName && normalizeAirlineName(mc.name) === prevFirstName)
                  || (prevFirstIata && String(mc.iata_code || '').trim().toUpperCase() === prevFirstIata);
                if (matchesOriginalCarrier) applyNewCarrier(seg);
              });
            }
            if (editedFlight.flight_number) {
              firstSegment.marketing_carrier_flight_number = editedFlight.flight_number;
            }
          }
        }
      });

      // Save the booking with updated flight data
      // CRITICAL: NEVER clear booking_reference or confirmation_number
      // Only update these fields if they have non-empty values
      const updateData = {
        flight_data: updatedFlightData,
        status: editingTrip[0]?.trip_status || editingTrip[0]?.status || 'pending'
      };
      /**
       * For Defera Zero/Capital, the booking row's pricing columns mirror Stripe — they're
       * written once by `defera-{zero,capital}-create-subscription` and must not be touched
       * by admin edits. Persisting the modal's `totalPrice/serviceFee/...` for these trips
       * silently inflates the displayed grand total by ~24% on every Save (see comment on
       * `isDeferaSubscriptionTrip`). Skip pricing fields entirely; Stripe is the source of
       * truth and FlightDetailsModal derives Cost Breakdown from `flight_data.base_amount`.
       *
       * For Slice Pay / installments / standard checkout, the same corruption pattern bites
       * if the admin opens the modal to edit anything else (flight credit, status,
       * confirmation number) and Save persists the recomputed pricing. J1TS39 was the
       * canary on 2026-06-09: the modal treated `bookings.total_amount` ($409, written by
       * change-flight as `grand_total/1.11`) as the base fare, recomputed
       * `service_fee=$32.72` / `grand_total=$441.72` / `hold_fee=$66.26` /
       * `grand_total_with_hold=$507.98`, and saved those values — overstating the trip's
       * gtwh by ~$54 vs. the change-flight canonical $453.99 and triggering a
       * Hold-Fee-(33%) self-heal artifact in the customer's Cost Breakdown.
       *
       * Gate: only persist pricing when the admin actually edited the "Total Trip Price"
       * input this session (see `hasAdminEditedPricingRef`). For untouched-pricing
       * sessions, leave the row's existing pricing columns alone.
       */
      if (!isDeferaSubscriptionTrip && hasAdminEditedPricingRef.current) {
        updateData.total_amount = totalPrice;
        updateData.service_fee = serviceFee;
        updateData.grand_total = grandTotal;
        updateData.hold_fee = holdFee;
        updateData.grand_total_with_hold = grandTotalWithHold;
      }
      
      // Only update confirmation_number if it has a non-empty value
      // This prevents accidentally clearing the airline confirmation
      if (editingTrip[0]?.confirmation_number && editingTrip[0].confirmation_number.trim() !== '') {
        updateData.confirmation_number = editingTrip[0].confirmation_number;
      }
      
      // Only update booking_reference if it has a non-empty value
      // This prevents accidentally clearing the Slice Pay reference
      if (editingTrip[0]?.booking_reference && editingTrip[0].booking_reference.trim() !== '') {
        updateData.booking_reference = editingTrip[0].booking_reference;
      }
      
      // Update backer_id
      updateData.backer_id = selectedBackerId && selectedBackerId !== 'none' ? selectedBackerId : null;
      
      // Include flight changes inside flight_data (avoids PostgREST schema cache issues)
      if (showFlightChanges && changedFlights.length > 0) {
        const changedFlightData = {
          slices: changedFlights.map((changed, index) => ({
            segments: [{
              origin: {
                iata_code: changed.departure_city,
                city_name: changed.departure_city
              },
              destination: {
                iata_code: changed.arrival_city,
                city_name: changed.arrival_city
              },
              departing_at: `${changed.departure_date} ${changed.departure_time}`,
              arriving_at: `${changed.arrival_date} ${changed.arrival_time}`,
              marketing_carrier: {
                name: changed.marketing_airline,
                ...(resolveCarrierBranding(changed.marketing_airline) || {})
              },
              marketing_carrier_flight_number: changed.flight_number
            }]
          }))
        };
        // Store inside flight_data instead of separate column
        updatedFlightData.flight_changes = changedFlightData;
      } else {
        // Clear flight changes if toggle is off
        delete updatedFlightData.flight_changes;
      }
      
      // CRITICAL: Store custom_title inside flight_data (same as flight_changes approach)
      // This avoids PostgREST schema cache issues with new columns
      if (customTitle && customTitle.trim()) {
        updatedFlightData.custom_title = customTitle.trim();
      } else {
        delete updatedFlightData.custom_title;
      }
      
      // Store cabin_class inside flight_data
      if (cabinClass && cabinClass.trim()) {
        updatedFlightData.cabin_class = cabinClass.trim();
      } else {
        delete updatedFlightData.cabin_class;
      }
      
      // Store final_repayment_date inside flight_data
      if (finalRepaymentDate) {
        updatedFlightData.final_repayment_date = finalRepaymentDate;
      } else {
        delete updatedFlightData.final_repayment_date;
      }
      
      // Store actual cost inside flight_data (overrides base fare in Accounting/Analytics when set)
      const acNum = actualCost !== '' ? parseFloat(actualCost) : null;
      if (acNum != null && !Number.isNaN(acNum) && acNum > 0) {
        updatedFlightData._actual_cost = acNum;
      } else {
        delete updatedFlightData._actual_cost;
      }

      // Store flight credit inside flight_data (stored as cents)
      const creditDollars = flightCredit !== '' ? parseFloat(flightCredit) : null;
      if (creditDollars != null && !Number.isNaN(creditDollars) && creditDollars > 0) {
        updatedFlightData._change_flight_credit_cents = Math.round(creditDollars * 100);
      } else {
        delete updatedFlightData._change_flight_credit_cents;
      }

      // Store amount refunded inside flight_data (dollars) — reduces recognized
      // profit in Accounting/Analytics by this amount when set.
      const refundedNum = amountRefunded !== '' ? parseFloat(amountRefunded) : null;
      if (refundedNum != null && !Number.isNaN(refundedNum) && refundedNum > 0) {
        updatedFlightData._amount_refunded = refundedNum;
      } else {
        delete updatedFlightData._amount_refunded;
      }

      // Store is_ticketed inside flight_data (progress bar only; does not change trip status)
      updatedFlightData.is_ticketed = !!(editingTrip[0]?.is_ticketed);

      // Store is_agency inside flight_data (admin ops flag — booked through a travel agency)
      updatedFlightData.is_agency = !!(editingTrip[0]?.is_agency);

      // Store upgrade info inside flight_data (shows an "Upgraded" card to the customer; the server
      // emails "You've been upgraded" the first time is_upgraded turns on — server/upgrade-email.js)
      const isUpgraded = !!(editingTrip[0]?.is_upgraded);
      updatedFlightData.is_upgraded = isUpgraded;
      if (isUpgraded) {
        updatedFlightData.upgraded_to = (editingTrip[0]?.upgraded_to || '').trim();
      } else {
        delete updatedFlightData.upgraded_to;
      }

      // Complimentary rewards besides a cabin upgrade (shown on the customer's trip and
      // Trips list; the server emails each newly added one — server/trip-reward-email.js)
      const rewards = editingTrip[0]?.is_rewarded ? rewardsToSave(editingTrip[0]?.complimentary_rewards) : [];
      if (rewards.length) {
        updatedFlightData.complimentary_rewards = rewards;
      } else {
        delete updatedFlightData.complimentary_rewards;
      }
      
      // Preserve existing _finance data (investor_cost & mileage_liability are auto-calculated by backend)
      if (updatedFlightData._finance) {
        updatedFlightData._finance = { ...updatedFlightData._finance };
      }
      
      // Update flight_data reference
      updateData.flight_data = updatedFlightData;
      
      // Use backend endpoint to bypass PostgREST schema cache issues with flight_changes
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Not authenticated');
      }
      
      // CRITICAL: Update ALL bookings in the trip
      // For primary booking (first one): send full updateData including flight_data
      // For secondary bookings: only send shared fields (pricing, status, custom_title)
      const updatePromises = trip.map(async (booking, index) => {
        let bookingUpdates;
        
        if (index === 0) {
          // Primary booking gets full update including flight_data
          bookingUpdates = updateData;
        } else {
          // Secondary bookings only get shared fields, NOT flight_data
          bookingUpdates = {
            status: updateData.status
          };
          /** Mirror the primary skip for Defera Zero/Capital, AND the J1TS39 untouched-pricing
           *  gate (`hasAdminEditedPricingRef`). When pricing wasn't admin-edited, the keys
           *  aren't on `updateData` either, so this branch becomes a no-op for pricing —
           *  but we re-check the conditions explicitly here so this guard can't drift from
           *  the primary write site if someone refactors. */
          if (!isDeferaSubscriptionTrip && hasAdminEditedPricingRef.current) {
            bookingUpdates.total_amount = updateData.total_amount;
            bookingUpdates.service_fee = updateData.service_fee;
            bookingUpdates.grand_total = updateData.grand_total;
            bookingUpdates.hold_fee = updateData.hold_fee;
            bookingUpdates.grand_total_with_hold = updateData.grand_total_with_hold;
          }
          
          // Also copy confirmation_number, booking_reference, and backer_id if present
          if (updateData.confirmation_number) {
            bookingUpdates.confirmation_number = updateData.confirmation_number;
          }
          if (updateData.booking_reference) {
            bookingUpdates.booking_reference = updateData.booking_reference;
          }
          // backer_id applies to all bookings in the trip
          if (updateData.backer_id !== undefined) {
            bookingUpdates.backer_id = updateData.backer_id;
          }
        }
        
        const response = await fetch('/api/admin/update-booking', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            bookingId: booking.id,
            updates: bookingUpdates
          })
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`Update booking ${booking.id} failed:`, response.status, errorText);
          let errorData;
          try {
            errorData = JSON.parse(errorText);
          } catch (e) {
            throw new Error(`Failed to update booking ${booking.id}: ${response.status} ${errorText}`);
          }
          throw new Error(errorData.error || `Failed to update booking ${booking.id}`);
        }
        
        return response.json();
      });
      
      // Wait for all updates to complete
      const updateResults = await Promise.all(updatePromises);

      /** The server saves the booking even when a customer-email side effect
       *  (trip-completed / trustpilot / cancelled / flight-change / upgraded) fails — it
       *  reports those failures in `email_warnings`. Surface them here instead
       *  of a blind success alert, so ops knows the customer was NOT emailed
       *  and can follow up (GQKN7H incident: 10 days of silent email failures
       *  behind '✅ Changes saved successfully!'). */
      const emailWarnings = updateResults.flatMap((r) => r?.email_warnings || []);
      if (emailWarnings.length > 0) {
        alert(
          '⚠️ Changes saved, BUT customer emails failed:\n\n'
          + emailWarnings.map((w) => `• ${w}`).join('\n')
          + '\n\nThe customer has NOT been notified — follow up manually or re-try once the email issue is fixed.'
        );
      } else {
        alert('✅ Changes saved successfully!');
      }
      
      // Refresh the trip data in parent component but keep modal open
      // This allows user to see their changes and continue editing if needed
      onSave();

      // If status was completed, show overlay to manually award miles
      const savedStatus = updateData.status || 'pending';
      if (savedStatus === 'completed' && trip?.[0]?.id) {
        setMilesAwardBookingId(trip[0].id);
        setShowMilesAwardOverlay(true);
      }
    } catch (error) {
      console.error('Failed to save changes:', error);
      alert('❌ Failed to save changes: ' + (error.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <motion.div
        className="fixed inset-0 z-50 flex items-start justify-center pt-20 pb-24 md:pb-4 md:pt-4 md:items-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}>

        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        
        <motion.div
          className="relative glass rounded-2xl p-6 max-w-5xl w-full max-h-[90vh] overflow-y-auto"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}>

          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-white">Trip Details</h2>
              {/* Display custom title or auto-generated route */}
              {customTitle ? (
                <p className="text-sm text-white/70 mt-1">{customTitle}</p>
              ) : editingTrip.length > 0 && (
                <p className="text-sm text-white/70 mt-1">
                  {editingTrip[0].departure_city || 'Unknown'} → {editingTrip[0].arrival_city || 'Unknown'}
                </p>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} className="hover:bg-white/10 rounded-full">
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="space-y-6">
            {/* Itinerary Pricing - Single Price for Entire Trip */}
            <Card className="glass-light border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Itinerary Pricing</CardTitle>
                <p className="text-sm text-gray-300">Set the total price for the entire trip</p>
              </CardHeader>
              <CardContent className="space-y-4">
                {isDeferaSubscriptionTrip && (
                  <div className="rounded-lg border border-brand-500/30 bg-brand-500/10 p-3 text-brand-100/90 text-xs">
                    <p className="font-medium mb-1">Pricing locked — Stripe-managed trip</p>
                    <p className="text-brand-100/70 leading-relaxed">
                      This is a Defera Zero / Capital trip. The base fare and fees come from{' '}
                      <code className="bg-brand-500/20 px-1 rounded">flight_data.base_amount</code> and the
                      Stripe subscription's <code className="bg-brand-500/20 px-1 rounded">unit_amount</code> /{' '}
                      <code className="bg-brand-500/20 px-1 rounded">metadata.total_amount_cents</code>.
                      To change pricing, use the customer-facing change-flight flow (which updates Stripe
                      atomically). Editing here would only desync the booking row from Stripe.
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">{isDeferaSubscriptionTrip ? 'Base Fare' : 'Total Trip Price'}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={totalPrice}
                      onChange={(e) => {
                        // Mark pricing as admin-edited so the recompute effect and save
                        // path know this session intentionally rewrites the row's pricing.
                        // See `hasAdminEditedPricingRef` comment for the J1TS39 background.
                        hasAdminEditedPricingRef.current = true;
                        setTotalPrice(parseFloat(e.target.value) || 0);
                      }}
                      disabled={isDeferaSubscriptionTrip}
                      className={isDeferaSubscriptionTrip
                        ? "glass text-gray-400 border-white/20"
                        : "glass text-white border-white/20 focus:border-white/40"}
                      placeholder="e.g., 1234.56" />
                    {!isDeferaSubscriptionTrip && !hasAdminEditedPricingRef.current && (
                      <p className="text-xs text-white/60">Edit this field to recompute fees and persist new pricing on save. Otherwise, the row's existing pricing stays untouched.</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Service Fee ({getServiceFeeLabel(totalPrice)})</Label>
                    <Input
                      type="number"
                      value={serviceFee.toFixed(2)}
                      disabled
                      className="glass text-gray-400 border-white/20"
                      placeholder="Auto-calculated" />

                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Grand Total</Label>
                    <Input
                      type="number"
                      value={grandTotal.toFixed(2)}
                      disabled
                      className="glass text-gray-400 border-white/20"
                      placeholder="Auto-calculated" />

                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Hold Fee (15%)</Label>
                    <Input
                      type="number"
                      value={holdFee.toFixed(2)}
                      disabled
                      className="glass text-gray-400 border-white/20"
                      placeholder="Reference only" />

                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Actual Cost</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={actualCost}
                      onChange={(e) => setActualCost(e.target.value)}
                      className="glass text-white border-white/20 focus:border-white/40"
                      placeholder="What you paid" />
                    <p className="text-xs text-white/60">Overrides base fare in Accounting</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Flight Credit ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={flightCredit}
                      onChange={(e) => setFlightCredit(e.target.value)}
                      className="glass text-white border-white/20 focus:border-white/40"
                      placeholder="0.00" />
                    <p className="text-xs text-white/60">Credit from flight changes (base fare only)</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Amount Refunded ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={amountRefunded}
                      onChange={(e) => setAmountRefunded(e.target.value)}
                      className="glass text-white border-white/20 focus:border-white/40"
                      placeholder="0.00" />
                    <p className="text-xs text-white/60">Cash refunded to the customer — reduces Net Profit in Accounting/Analytics</p>
                  </div>
                </div>
                <div className="pt-2 border-t border-white/10">
                  <div className="flex justify-between items-center">
                    <span className="text-white font-semibold">Total with Hold Fee:</span>
                    <span className="text-xl font-bold text-white">${fmtAmount(grandTotalWithHold)}</span>
                  </div>
                </div>
                
                {/* Profit Summary - Auto-calculated from backend */}
                <div className="pt-4 border-t border-white/10">
                  <div className="p-3 rounded-lg bg-white/5">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-white/60">Gross Profit (Service Fee):</span>
                        <span className="text-emerald-400">${fmtAmount(serviceFee)}</span>
                      </div>
                      {parseFloat(amountRefunded) > 0 && (
                        <div className="flex justify-between">
                          <span className="text-white/60">Amount Refunded:</span>
                          <span className="text-red-400">-${fmtAmount(parseFloat(amountRefunded))}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-white/60 mt-2">Investor cost & miles liability are auto-calculated</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Flight Details Section */}
            <Card className="glass-light border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  Flight Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Trip Status - only show for the first flight */}
                <div className="glass rounded-xl p-4 space-y-4">
                  <h4 className="font-semibold text-white">Trip Status & Confirmation</h4>
                  <div className="space-y-2">
                    <Label className="text-white">Overall Trip Status</Label>
                    <Select
                      value={editingTrip[0]?.trip_status || editingTrip[0]?.status || 'pending'}
                      onValueChange={(value) => handleFlightChange(0, 'trip_status', value)}>

                      <SelectTrigger className="glass text-white border-white/20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                        {/* The NMI installment cron sets this after a terminal
                            decline (migrations 068/073). Recoverable — the
                            customer's restart-plan flow flips it back to
                            Confirmed. Listed so admins can read AND set it;
                            escalate to Defaulted only for truly dead plans
                            (Defaulted auto-blacklists passengers). */}
                        <SelectItem value="delinquent">Delinquent (payments failing — recoverable)</SelectItem>
                        <SelectItem value="defaulted">Defaulted</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3">
                    <div>
                      <Label className="text-white">Ticketed</Label>
                      <p className="text-xs text-white/60 mt-0.5">Updates the progress bar only; trip status stays as above.</p>
                    </div>
                    <Switch
                      checked={!!editingTrip[0]?.is_ticketed}
                      onCheckedChange={(checked) => handleFlightChange(0, 'is_ticketed', checked)}
                      className="data-[state=checked]:bg-primary"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3">
                    <div>
                      <Label className="text-white">Agency</Label>
                      <p className="text-xs text-white/60 mt-0.5">Mark when this trip was booked through a travel agency.</p>
                    </div>
                    <Switch
                      checked={!!editingTrip[0]?.is_agency}
                      onCheckedChange={(checked) => handleFlightChange(0, 'is_agency', checked)}
                      className="data-[state=checked]:bg-primary"
                    />
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <Label className="text-white">Upgraded</Label>
                        <p className="text-xs text-white/60 mt-0.5">Shows an &quot;Upgraded&quot; card to the customer below the progress bar and emails them &quot;You&apos;ve been upgraded&quot; the first time this is switched on. Re-saves don&apos;t resend; a different cabin does.</p>
                      </div>
                      <Switch
                        checked={!!editingTrip[0]?.is_upgraded}
                        onCheckedChange={(checked) => handleFlightChange(0, 'is_upgraded', checked)}
                        className="data-[state=checked]:bg-primary"
                      />
                    </div>
                    {!!editingTrip[0]?.is_upgraded && (
                      <div className="space-y-2">
                        <Label className="text-white">Upgraded to</Label>
                        <Input
                          value={editingTrip[0]?.upgraded_to || ''}
                          onChange={(e) => handleFlightChange(0, 'upgraded_to', e.target.value)}
                          className="glass text-white border-white/20"
                          placeholder="e.g., First Class, Main Cabin Plus, Business"
                        />
                      </div>
                    )}
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <Label className="text-white">Rewarded</Label>
                        <p className="text-xs text-white/60 mt-0.5">Something complimentary besides a cabin upgrade, such as priority boarding or a lounge pass. Shows on the customer&apos;s trip and Trips list, and emails them the first time each reward is added. Re-saves don&apos;t resend.</p>
                      </div>
                      <Switch
                        checked={!!editingTrip[0]?.is_rewarded}
                        onCheckedChange={handleRewardedToggle}
                        className="data-[state=checked]:bg-primary"
                        aria-label="Rewarded"
                      />
                    </div>
                    {!!editingTrip[0]?.is_rewarded && (
                      <div className="space-y-3">
                        {(editingTrip[0]?.complimentary_rewards || []).map((reward, i) => {
                          const category = TRIP_REWARD_CATEGORIES.find((c) => c.id === reward.category) || TRIP_REWARD_CATEGORIES[0];
                          return (
                            <div key={reward.id || `new-${i}`} className="grid grid-cols-1 sm:grid-cols-[minmax(0,190px)_minmax(0,1fr)_auto] gap-2 sm:items-end">
                              <div className="space-y-1.5">
                                <Label className="text-white/80 text-xs">Category</Label>
                                <Select
                                  value={category.id}
                                  onValueChange={(value) => setRewardRows((rows) => rows.map((r, k) => (k === i ? { ...r, category: value } : r)))}
                                >
                                  <SelectTrigger className="glass text-white border-white/20" aria-label="Reward category">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {TRIP_REWARD_CATEGORIES.map((c) => (
                                      <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1.5">
                                <Label className="text-white/80 text-xs">Reward name</Label>
                                <Input
                                  value={reward.name || ''}
                                  onChange={(e) => {
                                    const { value } = e.target;
                                    setRewardRows((rows) => rows.map((r, k) => (k === i ? { ...r, name: value } : r)));
                                  }}
                                  className="glass text-white border-white/20"
                                  placeholder={category.example}
                                  aria-label="Reward name"
                                />
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="text-white/50 hover:text-red-300 hover:bg-white/5 justify-self-end"
                                onClick={() => setRewardRows((rows) => rows.filter((_, k) => k !== i))}
                                aria-label="Remove this reward"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          );
                        })}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="glass border-white/20 text-white/80 hover:text-white"
                          onClick={() => setRewardRows((rows) => [...rows, blankRewardRow()])}
                        >
                          <Plus className="w-3.5 h-3.5 mr-1.5" />
                          Add another reward
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Confirmation Number</Label>
                    <Input
                      value={editingTrip[0]?.confirmationNumber || editingTrip[0]?.confirmation_number || ''}
                      onChange={(e) => handleFlightChange(0, 'confirmation_number', e.target.value)}
                      className="glass text-white border-white/20"
                      placeholder="e.g., ABC123XYZ"
                    />
                    <p className="text-xs text-white/60">⚠️ Only visible to customers when trip status is "Completed" (fully paid off)</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">{ic.adminBookingRefLabel}</Label>
                    <Input
                      value={editingTrip[0]?.booking_reference || editingTrip[0]?.bookingReference || ''}
                      onChange={(e) => {
                        handleFlightChange(0, 'booking_reference', e.target.value);
                        handleFlightChange(0, 'bookingReference', e.target.value);
                      }}
                      className="glass text-white border-white/20 font-mono"
                      placeholder="e.g., UM3IKK"
                    />
                    <p className="text-xs text-white/60">{ic.adminBookingRefHint}</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-white">Final Repayment Date</Label>
                    <Input
                      type="date"
                      value={finalRepaymentDate}
                      onChange={(e) => setFinalRepaymentDate(e.target.value)}
                      className="glass text-white border-white/20"
                    />
                    <p className="text-xs text-white/60">Date when customer's final payment is due - trip ready for release after this date</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-white">Backer</Label>
                    <Select value={selectedBackerId} onValueChange={setSelectedBackerId}>
                      <SelectTrigger className="glass text-white border-white/20">
                        <SelectValue placeholder="Select backer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {backers.map(backer => (
                          <SelectItem key={backer.id} value={backer.id}>
                            {backer.firstName && backer.lastName 
                              ? `${backer.firstName} ${backer.lastName} (${backer.email})`
                              : backer.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-white/60">Backer who will earn 25% of the service fee for this trip</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-white">Custom Trip Title</Label>
                    <Input
                      value={customTitle}
                      onChange={(e) => setCustomTitle(e.target.value)}
                      className="glass text-white border-white/20"
                      placeholder="e.g., RIC → Tokyo"
                    />
                    <p className="text-xs text-white/60">Override the auto-generated trip title (leave blank to use airport codes)</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-white">Cabin Class</Label>
                    <Input
                      value={cabinClass}
                      onChange={(e) => setCabinClass(e.target.value)}
                      className="glass text-white border-white/20"
                      placeholder="e.g., Economy, Premium Economy, Business, First"
                    />
                    <p className="text-xs text-white/60">Override the cabin class displayed in Flight Details (bottom right corner)</p>
                  </div>
                  
                  {/* Flight Change Button */}
                  <div className="pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (!showFlightChanges) {
                          initializeFlightChanges();
                        } else {
                          setShowFlightChanges(false);
                          setChangedFlights([]);
                        }
                      }}
                      className={`w-full glass border-white/20 ${showFlightChanges ? 'bg-brand-500/20 border-brand-500/40 text-brand-300 hover:bg-brand-500/30' : 'text-white hover:bg-white/10'}`}
                    >
                      <AlertCircle className="w-4 h-4 mr-2" />
                      {showFlightChanges ? 'Remove Flight Changes' : 'Add Flight Changes'}
                    </Button>
                    {!showFlightChanges && (
                      <p className="text-xs text-white/60 mt-2">Use this to record airline-initiated schedule changes or delays</p>
                    )}
                  </div>
                </div>

                {editingTrip.map((flight, index) =>
                <motion.div
                  key={`${flight.id}-${index}`}
                  className="glass rounded-xl p-4 space-y-4"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}>

                    <h4 className="font-semibold text-white">Flight {index + 1}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-white">Airline</Label>
                        <Input
                        value={flight.marketing_airline || ''}
                        onChange={(e) => handleFlightChange(index, 'marketing_airline', e.target.value)}
                        className="glass text-white border-white/20" />

                      </div>
                      <div className="space-y-2">
                        <Label className="text-white">Flight Number</Label>
                        <Input
                        value={flight.flight_number || ''}
                        onChange={(e) => handleFlightChange(index, 'flight_number', e.target.value)}
                        className="glass text-white border-white/20" />

                      </div>
                      <div className="space-y-2">
                        <Label className="text-white">Departure City</Label>
                        <Input
                        value={flight.departure_city || ''}
                        onChange={(e) => handleFlightChange(index, 'departure_city', e.target.value)}
                        className="glass text-white border-white/20" />

                      </div>
                      <div className="space-y-2">
                        <Label className="text-white">Arrival City</Label>
                        <Input
                        value={flight.arrival_city || ''}
                        onChange={(e) => handleFlightChange(index, 'arrival_city', e.target.value)}
                        className="glass text-white border-white/20" />

                      </div>
                      <div className="space-y-2">
                        <Label className="text-white">Departure Date</Label>
                        <Input
                        type="date"
                        value={flight.departure_date || ''}
                        onChange={(e) => handleFlightChange(index, 'departure_date', e.target.value)}
                        className="glass text-white border-white/20" />

                      </div>
                      <div className="space-y-2">
                        <Label className="text-white">Departure Time</Label>
                        <Input
                        type="time"
                        value={flight.departure_time || ''}
                        onChange={(e) => handleFlightChange(index, 'departure_time', e.target.value)}
                        className="glass text-white border-white/20" />

                      </div>
                      <div className="space-y-2">
                        <Label className="text-white">Arrival Date</Label>
                        <Input
                        type="date"
                        value={flight.arrival_date || ''}
                        onChange={(e) => handleFlightChange(index, 'arrival_date', e.target.value)}
                        className="glass text-white border-white/20" />

                      </div>
                      <div className="space-y-2">
                        <Label className="text-white">Arrival Time</Label>
                        <Input
                        type="time"
                        value={flight.arrival_time || ''}
                        onChange={(e) => handleFlightChange(index, 'arrival_time', e.target.value)}
                        className="glass text-white border-white/20" />

                      </div>
                      {/* Total Price input removed as it's now a single trip price */}
                    </div>

                    {/* Segment Details: Aircraft, Flight #, Duration, Layover */}
                    {flight.sliceData?.segments && flight.sliceData.segments.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h5 className="text-sm font-medium text-white/80">Segment Details</h5>
                            <p className="text-xs text-white/60 mt-1">Edit aircraft type, flight numbers, duration, and layover city per segment.</p>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => handleAddSegment(index)}
                            className="h-8 px-4 rounded-full bg-primary text-white hover:bg-primary/90 font-medium"
                          >
                            <Plus className="w-3.5 h-3.5 mr-1.5" />
                            Add Segment
                          </Button>
                        </div>
                        <div className="space-y-4">
                          {flight.sliceData.segments.map((seg, segIdx) => (
                            <div key={segIdx} className="glass rounded-lg p-3 space-y-3">
                              <div className="flex items-center justify-between">
                                <h6 className="text-xs font-normal text-white/70">Segment {segIdx + 1}</h6>
                                {flight.sliceData.segments.length > 1 && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteSegment(index, segIdx)}
                                    className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                  >
                                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                                    Delete
                                  </Button>
                                )}
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-white/70 text-xs">Aircraft</Label>
                                  <Input
                                    value={seg.aircraft?.name || ''}
                                    onChange={(e) => handleSegmentChange(index, segIdx, 'aircraft', e.target.value)}
                                    className="glass text-white border-white/20 text-sm"
                                    placeholder="e.g., Airbus A321neo"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-white/70 text-xs">Flight Number</Label>
                                  <Input
                                    value={seg.marketing_carrier_flight_number || ''}
                                    onChange={(e) => handleSegmentChange(index, segIdx, 'flight_number', e.target.value)}
                                    className="glass text-white border-white/20 text-sm"
                                    placeholder="e.g., AA 5636"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-white/70 text-xs">Duration</Label>
                                  <Input
                                    value={formatDurationForInput(seg.duration)}
                                    onChange={(e) => handleSegmentChange(index, segIdx, 'duration', e.target.value)}
                                    className="glass text-white border-white/20 text-sm"
                                    placeholder="e.g., 3 hr 26 min"
                                  />
                                </div>
                                {segIdx < flight.sliceData.segments.length - 1 && (
                                  <>
                                    <div className="space-y-1">
                                      <Label className="text-white/70 text-xs">Layover City</Label>
                                      <Input
                                        value={seg.destination?.iata_code || ''}
                                        onChange={(e) => handleSegmentChange(index, segIdx, 'layover_city', e.target.value)}
                                        className="glass text-white border-white/20 text-sm"
                                        placeholder="e.g., PHX"
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <Label className="text-white/70 text-xs">Layover Duration</Label>
                                      <Input
                                        value={formatDurationForInput(flight.sliceData.segments[segIdx + 1]?.layover_duration)}
                                        onChange={(e) => handleSegmentChange(index, segIdx, 'layover_duration', e.target.value)}
                                        className="glass text-white border-white/20 text-sm"
                                        placeholder="e.g., 2 hr 35 min"
                                      />
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}
                
                {/* Changed Flights Section - Only show if flight changes are enabled */}
                {showFlightChanges && changedFlights.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-brand-500/30">
                    <div className="flex items-center gap-2 mb-4">
                      <AlertCircle className="w-5 h-5 text-brand-400" />
                      <h4 className="font-semibold text-brand-300">Changed Flight Details</h4>
                    </div>
                    <p className="text-sm text-white/60 mb-4">
                      These are the updated flight details after airline-initiated changes. Customers will see a side-by-side comparison of original vs. changed flights.
                    </p>
                    
                    {changedFlights.map((changed, index) => (
                      <motion.div
                        key={`changed-${index}`}
                        className="glass rounded-xl p-4 space-y-4 border border-brand-500/30 bg-brand-500/5"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <h4 className="font-semibold text-brand-300">Flight {index + 1} (Changed)</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label className="text-white">Airline</Label>
                            <Input
                              value={changed.marketing_airline || ''}
                              onChange={(e) => handleChangedFlightChange(index, 'marketing_airline', e.target.value)}
                              className="glass text-white border-brand-500/30 focus:border-brand-400"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-white">Flight Number</Label>
                            <Input
                              value={changed.flight_number || ''}
                              onChange={(e) => handleChangedFlightChange(index, 'flight_number', e.target.value)}
                              className="glass text-white border-brand-500/30 focus:border-brand-400"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-white">Departure City</Label>
                            <Input
                              value={changed.departure_city || ''}
                              onChange={(e) => handleChangedFlightChange(index, 'departure_city', e.target.value)}
                              className="glass text-white border-brand-500/30 focus:border-brand-400"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-white">Arrival City</Label>
                            <Input
                              value={changed.arrival_city || ''}
                              onChange={(e) => handleChangedFlightChange(index, 'arrival_city', e.target.value)}
                              className="glass text-white border-brand-500/30 focus:border-brand-400"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-white">Departure Date</Label>
                            <Input
                              type="date"
                              value={changed.departure_date || ''}
                              onChange={(e) => handleChangedFlightChange(index, 'departure_date', e.target.value)}
                              className="glass text-white border-brand-500/30 focus:border-brand-400"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-white">Departure Time</Label>
                            <Input
                              type="time"
                              value={changed.departure_time || ''}
                              onChange={(e) => handleChangedFlightChange(index, 'departure_time', e.target.value)}
                              className="glass text-white border-brand-500/30 focus:border-brand-400"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-white">Arrival Date</Label>
                            <Input
                              type="date"
                              value={changed.arrival_date || ''}
                              onChange={(e) => handleChangedFlightChange(index, 'arrival_date', e.target.value)}
                              className="glass text-white border-brand-500/30 focus:border-brand-400"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-white">Arrival Time</Label>
                            <Input
                              type="time"
                              value={changed.arrival_time || ''}
                              onChange={(e) => handleChangedFlightChange(index, 'arrival_time', e.target.value)}
                              className="glass text-white border-brand-500/30 focus:border-brand-400"
                            />
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-end gap-3 pt-6 pb-20 md:pb-0 border-t border-white/10">
            <Button
              variant="outline"
              onClick={onClose}
              className="glass border-white/20 text-white hover:bg-white/10 hover:text-white"
              disabled={isLoading}>

              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="bg-[var(--accent-color)] hover:bg-primary/90 text-white"
              disabled={isLoading}>

              {isLoading ? 'Saving...' : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
            </Button>
          </div>
        </motion.div>
      </motion.div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm &&
      <motion.div
        className="fixed inset-0 z-[70] flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}>

          <div
          className="absolute inset-0 bg-black/90 backdrop-blur-sm"
          onClick={() => setDeleteConfirm(null)} />

          
          <motion.div
          className="relative glass rounded-2xl p-6 max-w-md w-full"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}>

            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 bg-red-500/10 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Delete Passenger</h3>
              <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                Are you sure you want to remove <strong className="text-white">{deleteConfirm.passengerName}</strong> from this trip? 
                This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <Button
                variant="outline"
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 glass border-white/20 text-white hover:bg-white/10 hover:text-white"
                disabled={isLoading}>

                  Cancel
                </Button>
                <Button
                onClick={removePassenger}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                disabled={isLoading}>

                  {isLoading ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      }

      {/* Miles Award Overlay - shown after marking trip as Completed */}
      <AnimatePresence>
      {showMilesAwardOverlay && (
        <motion.div
          key="miles-award-overlay"
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-black/90 backdrop-blur-sm"
            onClick={() => !milesAwardLoading && (setShowMilesAwardOverlay(false), setMilesAwardBookingId(null))}
          />
          <motion.div
            className="relative glass rounded-2xl p-6 max-w-md w-full border border-brand-500/30"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
          >
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 bg-brand-500/20 rounded-full flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-brand-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Award Miles to Customer</h3>
              <p className="text-sm text-white/70 mb-6">
                If miles were not automatically awarded, click below to manually inject miles for this completed trip (2 miles per $1).
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => !milesAwardLoading && (setShowMilesAwardOverlay(false), setMilesAwardBookingId(null))}
                  className="flex-1 glass border-white/20 text-white hover:bg-white/10 hover:text-white"
                  disabled={milesAwardLoading}
                >
                  Skip
                </Button>
                <Button
                  onClick={handleAwardMiles}
                  className="flex-1 bg-brand-500 hover:bg-brand-600 text-white"
                  disabled={milesAwardLoading}
                >
                  {milesAwardLoading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Award Miles'}
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>
    </>);

}